/**
 * SenseCritiq - Web Chat Interface
 * Phase 2: app.sensecritiq.com/chat
 *
 * Full-page chat UI with:
 * - Conversation sidebar
 * - Streaming SSE message display
 * - Tool call indicators
 * - Inline citation cards
 * - File drag-and-drop upload
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

const C = {
  // Sidebar (dark)
  void:    '#030A3C',
  void2:   '#060D4A',
  void3:   '#0A1260',
  cyan:    '#00FFFF',
  sideBorder: 'rgba(255,255,255,0.10)',
  sideText:   '#CBD5E1',   // slate-300, ~7:1 on dark — was rgba white 50%
  sideTextActive: '#FFFFFF',
  sideTextMuted:  '#94A3B8', // slate-400, ~4.5:1 — was rgba white 28%
  // Chat area (light)
  chatBg:     '#FFFFFF',
  chatBg2:    '#F9FAFB',   // gray-50 — input area, assistant bubble
  chatBg3:    '#F3F4F6',   // gray-100 — textarea, table rows
  chatText:   '#111827',   // gray-900
  chatMuted:  '#6B7280',   // gray-500
  chatBorder: '#E5E7EB',   // gray-200
  chatBorderMed: '#D1D5DB',// gray-300
  userBubble: '#EEF2FF',   // indigo-50
  userBubbleBorder: '#C7D2FE', // indigo-200
  accentText: '#4338CA',   // indigo-700 — links, table headers
  // Shared
  magma:   '#FF5722',
  green:   '#059669',      // emerald-600
  amber:   '#D97706',      // amber-600
  // Legacy aliases kept for inline refs
  white:   '#F8F9FA',
  muted:   '#6B7280',
  muted2:  '#9CA3AF',
  border:  '#E5E7EB',
  border2: '#D1D5DB',
  cyanDim: 'rgba(0,255,255,0.10)',
  cyanBorder: 'rgba(0,255,255,0.15)',
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const s = {
  root: {
    display: 'flex',
    flex: 1,
    height: '100%',
    fontFamily: "'Inter', sans-serif",
    background: C.void,
    color: C.chatText,
    minWidth: 0,
  },

  // Sidebar
  sidebar: {
    width: 240,
    minWidth: 240,
    background: C.void2,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${C.sideBorder}`,
  },
  sidebarHeader: {
    padding: '16px 14px 12px',
    borderBottom: `1px solid ${C.sideBorder}`,
  },
  newChatBtnDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
    background: 'rgba(248,249,250,0.08)',
  },
  newChatBtn: {
    width: '100%',
    padding: '9px 12px',
    background: C.magma,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "'Satoshi','Inter',sans-serif",
    cursor: 'pointer',
    textAlign: 'left',
    letterSpacing: '-0.01em',
  },
  convList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  },
  convItem: (active) => ({
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    background: active ? 'rgba(255,255,255,0.11)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.07)'}`,
    borderLeft: `3px solid ${active ? C.magma : 'rgba(255,255,255,0.10)'}`,
    marginBottom: 6,
    transition: 'background 0.15s, border-color 0.15s',
  }),
  convTitle: (active) => ({
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    color: active ? C.sideTextActive : C.sideText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 6,
    letterSpacing: '-0.01em',
  }),
  convDate: {
    fontSize: 11,
    color: C.sideTextMuted,
    display: 'flex',
    gap: 5,
    alignItems: 'baseline',
    marginTop: 2,
  },
  convDateLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: 9,
    color: C.sideTextMuted,
    fontWeight: 700,
    flexShrink: 0,
  },

  // Main area
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    background: C.chatBg,
  },
  topBar: {
    padding: '12px 24px',
    borderBottom: `1px solid ${C.chatBorder}`,
    background: C.chatBg,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'Satoshi','Inter',sans-serif",
    color: C.chatText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    letterSpacing: '-0.01em',
  },

  // Messages
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.chatMuted,
    gap: 16,
    textAlign: 'center',
    padding: '0 40px',
  },
  emptyTitle: {
    fontFamily: "'Satoshi','Inter',sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: C.chatText,
    letterSpacing: '-0.02em',
  },
  emptySubtitle: {
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 420,
    color: C.chatMuted,
  },
  suggestionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 8,
    width: '100%',
    maxWidth: 500,
  },
  suggestionCard: {
    padding: '12px 14px',
    border: `1px solid ${C.chatBorder}`,
    borderRadius: 10,
    background: C.chatBg2,
    cursor: 'pointer',
    fontSize: 13,
    color: C.chatMuted,
    textAlign: 'left',
    lineHeight: 1.4,
    fontFamily: "'Inter',sans-serif",
  },

  // Message bubbles
  msgRow: (role) => ({
    display: 'flex',
    justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
    alignItems: 'flex-end',
    gap: 8,
  }),
  bubble: (role) => ({
    maxWidth: '72%',
    padding: '12px 16px',
    borderRadius: role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    background: role === 'user' ? C.userBubble : C.chatBg2,
    color: C.chatText,
    fontSize: 14,
    lineHeight: 1.65,
    border: role === 'user' ? `1px solid ${C.userBubbleBorder}` : `1px solid ${C.chatBorder}`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }),
  typingDots: { display:'inline-flex', alignItems:'center', gap:5, padding:'2px 0' },
  dot: (delay) => ({ width:7, height:7, borderRadius:'50%', background:'#94A3B8', display:'inline-block', animation:`scqDotBounce 1.2s ease-in-out ${delay}s infinite` }),
  avatar: (role) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: role === 'user' ? C.userBubble : C.void2,
    border: `1px solid ${role === 'user' ? C.chatBorderMed : C.sideBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: C.accentText,
    flexShrink: 0,
    overflow: 'hidden',
  }),

  // Tool indicator
  toolPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    background: '#EEF2FF',
    border: `1px solid #C7D2FE`,
    borderRadius: 20,
    fontSize: 12,
    color: C.accentText,
    fontWeight: 500,
    margin: '4px 0',
  },
  toolDot: (done) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: done ? C.green : C.amber,
    animation: done ? 'none' : 'scqPulse 1s infinite',
  }),

  // Markdown table
  tableWrap: { overflowX: 'auto', margin: '10px 0', borderRadius: 10, border: `1px solid ${C.chatBorderMed}` },
  mdTable:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  mdTh:      { padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, fontFamily: "'Satoshi','Inter',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase', color: C.accentText, background: '#EEF2FF', borderBottom: `1px solid ${C.chatBorderMed}`, whiteSpace: 'nowrap' },
  mdTd:      { padding: '9px 14px', color: C.chatText, fontSize: 13, borderBottom: `1px solid ${C.chatBorder}`, verticalAlign: 'top' },
  mdTrEven:  { background: 'transparent' },
  mdTrOdd:   { background: C.chatBg2 },
  // Markdown headings + misc
  mdH1:      { fontFamily: "'Satoshi','Inter',sans-serif", fontSize: 18, fontWeight: 700, color: C.chatText, margin: '14px 0 6px', letterSpacing: '-0.01em' },
  mdH2:      { fontFamily: "'Satoshi','Inter',sans-serif", fontSize: 15, fontWeight: 700, color: C.chatText, margin: '12px 0 4px' },
  mdH3:      { fontFamily: "'Satoshi','Inter',sans-serif", fontSize: 13, fontWeight: 700, color: C.accentText, margin: '10px 0 4px' },
  mdLi:      { paddingLeft: 12, color: C.chatText, fontSize: 14, lineHeight: 1.6, margin: '2px 0' },
  mdHr:      { border: 'none', borderTop: `1px solid ${C.chatBorder}`, margin: '12px 0' },

  // Quote card
  quoteCard: {
    margin: '8px 0',
    padding: '10px 14px',
    borderLeft: `3px solid ${C.accentText}`,
    background: '#EEF2FF',
    borderRadius: '0 8px 8px 0',
    fontSize: 13,
    color: C.chatText,
    fontStyle: 'italic',
    lineHeight: 1.55,
  },
  quoteMeta: {
    marginTop: 6,
    fontSize: 11,
    color: C.chatMuted,
    fontStyle: 'normal',
    fontWeight: 500,
  },

  // Input area
  inputArea: {
    padding: '14px 24px 18px',
    borderTop: `1px solid ${C.chatBorder}`,
    background: C.chatBg2,
  },
  dropZone: (dragging) => ({
    border: dragging ? `2px dashed ${C.accentText}` : '2px dashed transparent',
    borderRadius: 12,
    background: dragging ? '#EEF2FF' : 'transparent',
    transition: 'all 0.2s',
  }),
  inputRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: 12,
    border: `1.5px solid ${C.chatBorderMed}`,
    fontSize: 14,
    fontFamily: "'Inter',sans-serif",
    resize: 'none',
    outline: 'none',
    lineHeight: 1.5,
    maxHeight: 160,
    overflowY: 'auto',
    background: C.chatBg,
    color: C.chatText,
    transition: 'border-color 0.15s',
  },
  sendBtn: (disabled) => ({
    padding: '10px 20px',
    background: disabled ? C.chatBg3 : C.magma,
    color: disabled ? C.chatMuted : '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "'Satoshi','Inter',sans-serif",
    cursor: disabled ? 'not-allowed' : 'pointer',
    flexShrink: 0,
    height: 42,
    transition: 'background 0.15s',
  }),
  fileHint: {
    marginTop: 8,
    fontSize: 12,
    color: C.chatMuted,
    textAlign: 'center',
  },
  uploadBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    background: 'rgba(5,150,105,0.08)',
    border: '1px solid rgba(5,150,105,0.25)',
    borderRadius: 8,
    fontSize: 13,
    color: C.green,
    marginBottom: 8,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function smartDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday - 86400000);
  const startOfWeek = new Date(startOfToday - startOfToday.getDay() * 86400000);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d >= startOfToday)     return `Today, ${time}`;
  if (d >= startOfYesterday) return `Yesterday, ${time}`;
  if (d >= startOfWeek)      return `${d.toLocaleDateString([], { weekday: 'long' })}, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

const TOOL_LABELS = {
  upload_research_file: '⬆ Uploading file…',
  get_session_status: '⏳ Checking session…',
  get_synthesis: '🔬 Retrieving synthesis…',
  get_quotes: '💬 Fetching quotes…',
  search_research: '🔍 Searching research…',
  list_sessions: '📋 Listing sessions…',
  generate_report: '📄 Generating report…',
};


// Simple markdown-ish renderer: bold, blockquotes, code blocks, clickable URLs
// Inline rich text: bold, URLs
function renderInline(text, key) {
  const combined = [];
  let lastIndex = 0;
  const regex = /(\*\*[^*]+\*\*)|(https?:\/\/[^\s)>\]"]+)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) combined.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    if (match[1]) combined.push({ type: 'bold', value: match[1].slice(2, -2) });
    else          combined.push({ type: 'url',  value: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) combined.push({ type: 'text', value: text.slice(lastIndex) });
  return (
    <span key={key}>
      {combined.map((part, j) => {
        if (part.type === 'bold') return <strong key={j}>{part.value}</strong>;
        if (part.type === 'url') {
          const isReport = part.value.includes('/reports/') || part.value.includes('r2.cloudflarestorage') || part.value.includes('X-Amz-Signature');
          return <a key={j} href={part.value} target="_blank" rel="noopener noreferrer" style={{ color: C.accentText, textDecoration: 'underline' }}>{isReport ? '📄 Download Report' : part.value}</a>;
        }
        return part.value;
      })}
    </span>
  );
}

function isTableRow(line) { return line.trim().startsWith('|') && line.trim().endsWith('|'); }
function isSeparator(line) { return /^\|[\s\-:|]+\|$/.test(line.trim()); }
function parseRow(line) {
  return line.trim().split('|').filter((_, i, a) => i > 0 && i < a.length - 1).map(c => c.trim());
}

function renderContent(text) {
  const lines = text.split('\n');

  // Group lines into segments: 'table' or 'text'
  const segments = [];
  let i = 0;
  while (i < lines.length) {
    if (isTableRow(lines[i])) {
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i])) { tableLines.push(lines[i]); i++; }
      segments.push({ type: 'table', lines: tableLines });
    } else {
      const textLines = [];
      while (i < lines.length && !isTableRow(lines[i])) { textLines.push(lines[i]); i++; }
      segments.push({ type: 'text', lines: textLines });
    }
  }

  return segments.map((seg, si) => {
    if (seg.type === 'table') {
      const rows = seg.lines.filter(l => !isSeparator(l));
      if (rows.length < 2) {
        // Not a real table — fall back to text
        return <span key={si}>{rows.join('\n')}</span>;
      }
      const headers = parseRow(rows[0]);
      const body    = rows.slice(1);
      return (
        <div key={si} style={s.tableWrap}>
          <table style={s.mdTable}>
            <thead>
              <tr>{headers.map((h, j) => <th key={j} style={s.mdTh}>{renderInline(h, j)}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} style={ri % 2 === 0 ? s.mdTrEven : s.mdTrOdd}>
                  {parseRow(row).map((cell, ci) => <td key={ci} style={s.mdTd}>{renderInline(cell, ci)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Text segment — render line by line
    return seg.lines.map((line, li) => {
      const key = `${si}-${li}`;
      if (line.startsWith('> ')) return <div key={key} style={s.quoteCard}>{line.slice(2)}</div>;
      if (/^#{1,3} /.test(line)) {
        const level = line.match(/^(#{1,3}) /)[1].length;
        const txt = line.replace(/^#{1,3} /, '');
        const hs = [s.mdH1, s.mdH2, s.mdH3][level - 1];
        return <div key={key} style={hs}>{renderInline(txt, 0)}</div>;
      }
      if (/^[-*] /.test(line)) {
        return <div key={key} style={s.mdLi}>· {renderInline(line.slice(2), 0)}</div>;
      }
      if (line.trim() === '---' || line.trim() === '***') {
        return <hr key={key} style={s.mdHr} />;
      }
      if (line.trim() === '') return <div key={key} style={{ height: 8 }} />;
      return <span key={key}>{renderInline(line, 0)}{'\n'}</span>;
    });
  });
}

// ── Components ────────────────────────────────────────────────────────────────
function ToolIndicator({ name, done, count, doneCount, downloadUrl, reportFormat }) {
  const isNotion = reportFormat === 'notion';
  const total = count || 1;
  const completed = doneCount || 0;
  const allDone = done && (total === 1 || completed >= total);
  const label = allDone
    ? (TOOL_LABELS[name] || name).replace('…', ' ✓')
    : (TOOL_LABELS[name] || name);
  const countBadge = total > 1
    ? ` ×${allDone ? total : completed + '/' + total}`
    : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
      <div style={s.toolPill}>
        <div style={s.toolDot(allDone)} />
        {label}{countBadge}
      </div>
      {done && downloadUrl && (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            background: isNotion ? C.void3 : C.magma,
            color: '#fff',
            borderRadius: 8,
            border: isNotion ? `1px solid ${C.border2}` : 'none',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: "'Satoshi','Inter',sans-serif",
            textDecoration: 'none',
          }}
        >
          {isNotion ? '🔲 Open in Notion' : '📄 Download Report'}
        </a>
      )}
    </div>
  );
}

function Message({ msg, userInitial }) {
  if (msg.role === 'tool') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <ToolIndicator name={msg.toolName} done={msg.done} count={msg.count} doneCount={msg.doneCount} downloadUrl={msg.downloadUrl} reportFormat={msg.reportFormat} />
      </div>
    );
  }
  return (
    <div style={s.msgRow(msg.role)}>
      {msg.role === 'assistant' && (
        <div style={s.avatar('assistant')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none" width="16" height="16">
            <circle cx="14"    cy="4"     r="1.10" fill="#00FFFF" opacity="0.80"/>
            <circle cx="17.42" cy="4.60"  r="1.30" fill="#00FFFF" opacity="0.90"/>
            <circle cx="20.43" cy="6.34"  r="1.00" fill="#00FFFF" opacity="0.70"/>
            <circle cx="22.66" cy="9"     r="1.40" fill="#00FFFF" opacity="0.95"/>
            <circle cx="23.85" cy="12.26" r="1.20" fill="#00FFFF" opacity="0.85"/>
            <circle cx="23.85" cy="15.74" r="1.50" fill="#00FFFF" opacity="1.00"/>
            <circle cx="22.66" cy="19"    r="1.10" fill="#00FFFF" opacity="0.75"/>
            <circle cx="20.43" cy="21.66" r="1.35" fill="#00FFFF" opacity="0.90"/>
            <circle cx="17.42" cy="23.40" r="0.90" fill="#00FFFF" opacity="0.60"/>
            <circle cx="14"    cy="24"    r="1.20" fill="#00FFFF" opacity="0.80"/>
            <circle cx="10.58" cy="23.40" r="1.40" fill="#00FFFF" opacity="0.92"/>
            <circle cx="7.57"  cy="21.66" r="1.00" fill="#00FFFF" opacity="0.68"/>
            <circle cx="5.34"  cy="19"    r="1.30" fill="#00FFFF" opacity="0.85"/>
            <circle cx="4.15"  cy="15.74" r="1.10" fill="#00FFFF" opacity="0.75"/>
            <circle cx="4.15"  cy="12.26" r="1.45" fill="#00FFFF" opacity="0.95"/>
            <circle cx="5.34"  cy="9"     r="0.95" fill="#00FFFF" opacity="0.65"/>
            <circle cx="7.57"  cy="6.34"  r="1.25" fill="#00FFFF" opacity="0.88"/>
            <circle cx="10.58" cy="4.60"  r="1.10" fill="#00FFFF" opacity="0.78"/>
          </svg>
        </div>
      )}
      <div style={s.bubble(msg.role)}>
        {msg.role === 'assistant' && msg.streaming && !msg.content ? (
          <span style={s.typingDots}>
            <span style={s.dot(0)} /><span style={s.dot(0.2)} /><span style={s.dot(0.4)} />
          </span>
        ) : (
          <>
            {msg.role === 'assistant' ? renderContent(msg.content) : msg.content}
            {msg.streaming && msg.content && <span style={{ opacity: 0.5 }}>▌</span>}
          </>
        )}
      </div>
      {msg.role === 'user' && (
        <div style={s.avatar('user')}>{userInitial}</div>
      )}
    </div>
  );
}

// ── Main Chat component ───────────────────────────────────────────────────────
export default function Chat() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const location = useLocation();
  const userInitial = user?.firstName
    ? (user.firstName[0] + (user.lastName?.[0] || '')).toUpperCase()
    : (user?.emailAddresses?.[0]?.emailAddress?.[0] || 'U').toUpperCase();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { name, sessionId }
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pollingRef = useRef(null);
  const autoContextRef = useRef(location.state || null);

  const loadConversations = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/v1/conversations?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setConversations(await res.json());
    } catch (e) {
      console.error('Failed to load conversations', e);
    }
  }, [getToken]);

  const loadConversation = useCallback(async (convId) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/v1/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const msgs = await res.json();
      setMessages(msgs.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        tool_name: m.tool_name,
      })));
    } catch (e) {
      console.error('Failed to load conversation messages', e);
    }
  }, [getToken]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Clear any active polling on unmount
  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // Auto-open a scoped chat when navigated from a project or session
  useEffect(() => {
    const ctx = autoContextRef.current;
    if (!ctx) return;
    window.history.replaceState({}, document.title);
    const msg = ctx.projectName
      ? `I want to explore the "${ctx.projectName}" project. What are the key themes and findings across all sessions?`
      : `Summarise the key findings and themes from the "${ctx.sessionName}" session.`;
    sendMessage(msg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function newChat() {
    setActiveConvId(null);
    setMessages([]);
    setInput('');
    setStreaming(false);
    setUploading(false);
    setUploadProgress(null);
    setPendingFile(null);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  async function sendMessage(text) {
    if (!text.trim() || streaming) return;

    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    // Placeholder for streaming assistant message
    const assistantId = Date.now() + 1;
    setMessages(prev => [...prev, { role: 'assistant', content: '', id: assistantId, streaming: true }]);

    try {
      const token = await getToken();
      const body = {
        message: pendingFile ? `${text}\n\n[File attached: ${pendingFile.name}]` : text,
        conversation_id: activeConvId,
      };
      setPendingFile(null);

      const res = await fetch(`${API_BASE}/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            handleSSEEvent(event, assistantId);
          } catch (e) {
            // malformed chunk, skip
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Something went wrong. Please try again.', streaming: false }
          : m
      ));
    } finally {
      setStreaming(false);
      await loadConversations();
    }
  }

  function handleSSEEvent(event, assistantId) {
    if (event.type === 'text') {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: m.content + event.delta }
          : m
      ));
    } else if (event.type === 'tool_start') {
      setMessages(prev => {
        const withoutAssistant = prev.filter(m => m.id !== assistantId);
        const existing = withoutAssistant.findLast?.(m => m.role === 'tool' && m.toolName === event.name)
          ?? withoutAssistant.slice().reverse().find(m => m.role === 'tool' && m.toolName === event.name);
        if (existing) {
          return [
            ...withoutAssistant.map(m => m === existing ? { ...m, count: (m.count || 1) + 1, done: false } : m),
            { role: 'assistant', content: '', id: assistantId, streaming: true },
          ];
        }
        return [
          ...withoutAssistant,
          { role: 'tool', toolName: event.name, done: false, count: 1, doneCount: 0, id: `tool-${event.name}-${Date.now()}` },
          { role: 'assistant', content: '', id: assistantId, streaming: true },
        ];
      });
    } else if (event.type === 'tool_result') {
      const downloadUrl =
        event.name === 'generate_report' && event.result?.download_url
          ? event.result.download_url
          : null;
      const reportFormat =
        event.name === 'generate_report' && event.result?.format
          ? event.result.format
          : null;
      setMessages(prev => {
        let marked = false;
        return prev.map(m => {
          if (!marked && m.role === 'tool' && m.toolName === event.name) {
            marked = true;
            const newDoneCount = (m.doneCount || 0) + 1;
            const total = m.count || 1;
            const allDone = newDoneCount >= total;
            return { ...m, doneCount: newDoneCount, done: allDone, ...(downloadUrl ? { downloadUrl, reportFormat } : {}) };
          }
          return m;
        });
      });
    } else if (event.type === 'error') {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: event.message || 'Something went wrong. Please try again.', streaming: false }
          : m
      ));
    } else if (event.type === 'done') {
      setActiveConvId(event.conversation_id);
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, streaming: false } : m
      ));
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  // Auto-resize textarea
  function handleInput(e) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  }

  // Drag-and-drop
  function handleDragOver(e) { e.preventDefault(); setDragging(true); }
  function handleDragLeave() { setDragging(false); }

  async function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    await uploadFile(file);
  }

  async function handleFileInput(e) {
    const file = e.target.files[0];
    if (!file) return;
    await uploadFile(file);
  }

  async function uploadFile(file) {
    const sessionName = file.name.replace(/\.[^/.]+$/, ''); // strip extension
    setUploading(true);
    setUploadProgress({ name: file.name, sessionId: null });

    const uploadMsgId = Date.now();
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `⬆ Uploading **${file.name}**…`,
      id: uploadMsgId,
    }]);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_name', sessionName);

      const res = await fetch(`${API_BASE}/v1/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const { session_id } = await res.json();

      setUploadProgress({ name: file.name, sessionId: session_id });
      setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
        ...m,
        content: `✅ **${file.name}** uploaded — analysing now. I'll let you know when it's ready.`,
      } : m));

      // Poll status directly — no need to route through Claude
      if (pollingRef.current) clearInterval(pollingRef.current);
      let attempts = 0;
      const MAX_ATTEMPTS = 60; // 5 minutes at 5s intervals

      pollingRef.current = setInterval(async () => {
        attempts++;
        try {
          const t = await getToken();
          const statusRes = await fetch(`${API_BASE}/v1/sessions/${session_id}/status`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          if (!statusRes.ok) return;
          const { status } = await statusRes.json();

          if (status === 'ready') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setUploadProgress(null);
            await sendMessage(`Session ${session_id} ("${sessionName}") is ready. Please retrieve and present the synthesis.`);
          } else if (status === 'failed' || attempts >= MAX_ATTEMPTS) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setUploadProgress(null);
            const reason = status === 'failed'
              ? `❌ Processing failed for **${sessionName}**. Check the session for details.`
              : `⚠️ Processing **${sessionName}** is taking longer than expected. Ask me to check the status when ready.`;
            setMessages(prev => [...prev, { role: 'assistant', content: reason, id: Date.now() }]);
          }
        } catch (e) {
          // Network hiccup — keep polling
        }
      }, 5000);

    } catch (err) {
      setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
        ...m,
        content: `❌ Upload failed for **${file.name}**. Please try again.`,
      } : m));
    } finally {
      setUploading(false);
    }
  }

  const SUGGESTIONS = [
    'What have users said about onboarding?',
    'Show me themes from my latest session',
    'Search for quotes about checkout friction',
    'Summarise what I learned this month',
  ];

  const showEmpty = messages.length === 0;

  return (
    <div style={s.root}>
      {/* Sidebar */}
      <div style={s.sidebar}>
        <div style={s.sidebarHeader}>
          <button
            style={{ ...s.newChatBtn, ...(activeConvId === null && messages.length === 0 ? s.newChatBtnDisabled : {}) }}
            onClick={newChat}
            disabled={activeConvId === null && messages.length === 0}
            title={activeConvId === null && messages.length === 0 ? 'You\'re already in a new conversation' : undefined}
          >
            + New conversation
          </button>
        </div>
        <div style={s.convList}>
          {conversations.length === 0 && (
            <div style={{ padding: '12px 8px', fontSize: 12, color: C.muted2 }}>
              No conversations yet
            </div>
          )}
          {conversations.map(conv => (
            <div
              key={conv.id}
              style={s.convItem(conv.id === activeConvId)}
              onClick={() => {
                setActiveConvId(conv.id);
                loadConversation(conv.id);
              }}
            >
              <div style={s.convTitle(conv.id === activeConvId)}>
                {conv.title || 'Untitled conversation'}
              </div>
              <div style={s.convDate}>
                <span style={s.convDateLabel}>Last activity</span>{smartDate(conv.updated_at)}
              </div>
              <div style={s.convDate}>
                <span style={s.convDateLabel}>Created</span>{smartDate(conv.created_at)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div style={s.main}>
        <div style={s.topBar}>
          <span>Research Assistant</span>
          {streaming && (
            <span style={{ fontSize: 12, color: C.accentText, fontWeight: 400, opacity: 0.7 }}>
              Thinking…
            </span>
          )}
        </div>

        <div style={s.messagesArea} className="scq-chat-area">
          {showEmpty ? (
            <div style={s.emptyState}>
              <div style={s.emptyTitle}>What would you like to explore?</div>
              <div style={s.emptySubtitle}>
                Upload a research recording, search across your sessions, or ask
                anything about your research history.
              </div>
              <div style={s.suggestionsGrid}>
                {SUGGESTIONS.map((s_, i) => (
                  <button
                    key={i}
                    style={s.suggestionCard}
                    onClick={() => sendMessage(s_)}
                  >
                    {s_}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map(msg => <Message key={msg.id} msg={msg} userInitial={userInitial} />)
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={s.inputArea}>
          <div
            style={s.dropZone(dragging)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploading && uploadProgress && (
              <div style={s.uploadBadge}>
                <span style={{animation: 'pulse 1s infinite'}}>⬆</span> Uploading {uploadProgress.name}…
              </div>
            )}
            <div style={s.inputRow}>
              {/* Hidden file input for click-to-upload */}
              <input
                type="file"
                id="file-input"
                style={{ display: 'none' }}
                accept=".mp3,.mp4,.wav,.m4a,.txt,.pdf,.docx"
                onChange={handleFileInput}
              />
              <label
                htmlFor="file-input"
                style={{
                  padding: '10px 12px',
                  background: C.void3,
                  border: `1.5px solid ${C.border2}`,
                  borderRadius: 10,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: 16,
                  flexShrink: 0,
                  height: 42,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: uploading ? 0.5 : 1,
                }}
                title="Upload a recording or transcript"
              >
                📎
              </label>
              <textarea
                ref={textareaRef}
                style={s.textarea}
                rows={1}
                placeholder="Ask about your research, or drop a file to upload…"
                value={input}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                disabled={streaming || uploading}
              />
              <button
                style={s.sendBtn(!input.trim() || streaming || uploading)}
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || streaming || uploading}
              >
                Send
              </button>
            </div>
            <div style={s.fileHint}>
              Drop or click 📎 to upload a recording or transcript · Enter to send
            </div>
          </div>
        </div>
      </div>

      <style>{`
        textarea::placeholder { color: rgba(248,249,250,0.25); }
        @keyframes scqDotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40%            { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
